import { useToast } from '@/hooks/useToast';
import { useWhatsAppConfig } from '@/hooks/useWhatsAppConfig';
import facebookService from '@/services/facebookService';
import { projectsService } from '@/services/projectsService';
import { loadFacebookSDK } from '@/utils/loadFacebookSDK';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

/**
 * WhatsApp Quality Rating styles
 */
const QUALITY_RATING_STYLES = {
  GREEN: 'bg-primary-100 text-primary-800',
  YELLOW: 'bg-yellow-100 text-yellow-800',
  RED: 'bg-red-100 text-red-800',
  NA: 'bg-gray-100 text-gray-800',
};

/**
 * WhatsApp Name Status styles
 */
const NAME_STATUS_STYLES = {
  APPROVED: 'bg-green-100 text-green-800',
  PENDING_REVIEW: 'bg-yellow-100 text-yellow-800',
  DECLINED: 'bg-red-100 text-red-800',
  EXPIRED: 'bg-red-100 text-red-800',
  AVAILABLE_WITHOUT_REVIEW: 'bg-gray-100 text-gray-800',
  NONE: 'bg-gray-100 text-gray-800',
};

/**
 * WhatsApp Configurations page component
 * @returns {JSX.Element} WhatsAppConfigurations component
 */
const WhatsAppConfigurations = () => {
  const { projectId } = useParams();
  const { showSuccess, showError, showLoading, dismissToast } = useToast();
  const queryClient = useQueryClient();

  // Facebook OAuth constants
  const FACEBOOK_APP_ID = import.meta.env.VITE_FACEBOOK_APP_ID;
  const FACEBOOK_CONFIG_ID = import.meta.env.VITE_FACEBOOK_CONFIG_ID;

  // Fetch project details to check isWhatsappConnected
  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsService.getProject(projectId),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // API returns { success: true, data: { ... } }
  const actualProject = projectData?.data;
  const isWhatsappConnected = actualProject?.isWhatsappConnected === true;

  // WhatsApp configuration hook - only fetch if WhatsApp is connected
  const {
    whatsappConfig,
    loading: whatsappConfigLoading,
    error: whatsappConfigError,
    fetchWhatsAppConfig,
    clearError: clearWhatsAppConfigError,
  } = useWhatsAppConfig(projectId, { enabled: isWhatsappConnected });

  // State management
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('');
  const [showPaymentStep, setShowPaymentStep] = useState(false);
  const [newConnectionConfigs, setNewConnectionConfigs] = useState({
    businessId: '',
    whatsappBusinessAccountId: '',
  });
  const messageEventDataRef = useRef({});

  // Initialize Facebook SDK and message event listener
  useEffect(() => {
    if (FACEBOOK_APP_ID) {
      // Session logging message event listener
      window.addEventListener('message', event => {
        if (!event.origin.endsWith('facebook.com')) return;
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'WA_EMBEDDED_SIGNUP') {
            messageEventDataRef.current = data; // Store in ref
          }
        } catch {
          console.error('Error parsing message event data:', event.data);
        }
      });

      loadFacebookSDK(FACEBOOK_APP_ID);
    }
  }, [FACEBOOK_APP_ID]);

  // Handle errors from hook
  useEffect(() => {
    if (whatsappConfigError) {
      showError(whatsappConfigError);
      clearWhatsAppConfigError();
    }
  }, [whatsappConfigError, showError, clearWhatsAppConfigError]);

  // Facebook OAuth handlers
  const handleWhatsAppConnect = () => {
    if (!FACEBOOK_APP_ID || !FACEBOOK_CONFIG_ID) {
      showError(
        'Facebook SDK configuration is missing. Please check your environment variables.'
      );
      return;
    }

    setIsConnecting(true);
    setConnectionStatus('Connecting to WhatsApp Business...');

    window.FB.login(
      response => {
        if (response.authResponse) {
          // Extract data from Facebook response and message event
          const messageData = messageEventDataRef.current?.data;
          const authResponse = response.authResponse;

          // Prepare callback data according to the required structure
          const callbackData = {
            code: authResponse.code,
            projectId: projectId,
            whatsappBusinessAccountId: messageData?.waba_id,
            phoneNumberId: messageData?.phone_number_id,
            businessId: messageData?.business_id,
          };

          const loadingToast = showLoading('Connecting WhatsApp Business...');

          facebookService
            .facebookCallback(callbackData)
            .then(() => {
              dismissToast(loadingToast);
              showSuccess('WhatsApp Business connected successfully!');
              setConnectionStatus('WhatsApp Business connected successfully!');

              // Store configs for the payment step
              setNewConnectionConfigs({
                businessId: callbackData.businessId,
                whatsappBusinessAccountId:
                  callbackData.whatsappBusinessAccountId,
              });

              // Show payment step
              setShowPaymentStep(true);

              // Refresh the configuration data and project data
              fetchWhatsAppConfig();
              queryClient.invalidateQueries({
                queryKey: ['project', projectId],
              });
            })
            .catch(() => {
              dismissToast(loadingToast);
              showError(
                'Failed to connect WhatsApp Business. Please try again.'
              );
              setConnectionStatus('Connection failed. Please try again.');
            })
            .finally(() => {
              setIsConnecting(false);
            });
        } else {
          setConnectionStatus('Connection failed. Please try again.');
          setIsConnecting(false);
        }
      },
      {
        config_id: FACEBOOK_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          sessionInfoVersion: '3',
          featureType: 'whatsapp_business_app_onboarding',
        },
      }
    );
  };

  const isLoading = whatsappConfigLoading;

  return (
    <div className="min-h-screen bg-gray-50">
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75">
          <div className="flex items-center bg-white rounded-lg p-6">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span className="text-gray-700">
              Loading WhatsApp configuration...
            </span>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                WhatsApp Configurations
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage your WhatsApp Business API configuration for this project
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-0 mt-6">
          <div className="space-y-6">
            {/* WhatsApp Config Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">
                WhatsApp Business Configuration
              </h2>
            </div>

            {/* WhatsApp Config Content */}
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              {showPaymentStep && (
                <div className="px-4 py-8 sm:px-6 text-center">
                  <div className="mx-auto h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <svg
                      className="h-6 w-6 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Connection Successful!
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
                    Your WhatsApp Business account is connected. Now, please
                    ensure you have a payment method added on Facebook to start
                    sending messages.
                  </p>

                  <div className="mt-8 space-y-3 max-w-xs mx-auto">
                    <a
                      href={`https://business.facebook.com/billing_hub/accounts/details?asset_id=${newConnectionConfigs.whatsappBusinessAccountId}&business_id=${newConnectionConfigs.businessId}&placement=standalone&global_scope_id=${newConnectionConfigs.businessId}&account_type=whatsapp-business-account`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      <svg
                        className="h-5 w-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                        />
                      </svg>
                      Add Payment Method on Facebook
                    </a>

                    <button
                      onClick={() => setShowPaymentStep(false)}
                      className="w-full inline-flex justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}

              {!showPaymentStep && !whatsappConfig && (
                <div className="text-center py-12">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No WhatsApp Configuration
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Connect your WhatsApp Business account to start sending
                    messages.
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={handleWhatsAppConnect}
                      disabled={isConnecting}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isConnecting ? (
                        <div className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Connecting...
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <svg
                            className="h-5 w-5 mr-2"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                          </svg>
                          Connect WhatsApp Business
                        </div>
                      )}
                    </button>
                  </div>

                  {connectionStatus && (
                    <div
                      className={`text-sm text-center p-3 rounded-md mt-4 ${
                        connectionStatus.includes('successfully')
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                      }`}
                    >
                      {connectionStatus}
                    </div>
                  )}
                </div>
              )}

              {!showPaymentStep && whatsappConfig && (
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-green-600 truncate">
                          WhatsApp Business Connected
                        </p>
                        <div className="ml-2 flex-shrink-0 flex">
                          <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Connected
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 space-y-2">
                        {whatsappConfig.verifiedName && (
                          <p className="text-sm text-gray-500">
                            <span className="font-medium">Verified Name:</span>{' '}
                            {whatsappConfig.verifiedName}{' '}
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${NAME_STATUS_STYLES[whatsappConfig.nameStatus] || NAME_STATUS_STYLES.NONE}`}
                            >
                              {whatsappConfig.nameStatus}
                            </span>
                          </p>
                        )}
                        {whatsappConfig.displayPhoneNumber && (
                          <p className="text-sm text-gray-500">
                            <span className="font-medium">
                              Display Phone Number:
                            </span>{' '}
                            {whatsappConfig.displayPhoneNumber}
                          </p>
                        )}
                        {whatsappConfig.messagingLimitTier && (
                          <p className="text-sm text-gray-500">
                            <span className="font-medium">
                              Messaging Limit Tier:
                            </span>{' '}
                            {whatsappConfig.messagingLimitTier}
                          </p>
                        )}
                        {whatsappConfig.qualityRating && (
                          <p className="text-sm text-gray-500">
                            <span className="font-medium">Quality Rating:</span>{' '}
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${QUALITY_RATING_STYLES[whatsappConfig.qualityRating] || QUALITY_RATING_STYLES.NA}`}
                            >
                              {whatsappConfig.qualityRating}
                            </span>
                          </p>
                        )}
                        {whatsappConfig.phoneNumber && (
                          <p className="text-sm text-gray-500">
                            <span className="font-medium">Phone:</span>{' '}
                            {whatsappConfig.phoneNumber}
                          </p>
                        )}
                        {whatsappConfig.whatsappBusinessAccountId && (
                          <p className="text-sm text-gray-500">
                            <span className="font-medium">
                              Business Account ID:
                            </span>{' '}
                            {whatsappConfig.whatsappBusinessAccountId}
                          </p>
                        )}
                        {whatsappConfig.phoneNumberId && (
                          <p className="text-sm text-gray-500">
                            <span className="font-medium">
                              Phone Number ID:
                            </span>{' '}
                            {whatsappConfig.phoneNumberId}
                          </p>
                        )}
                        {whatsappConfig.businessId && (
                          <p className="text-sm text-gray-500">
                            <span className="font-medium">Business ID:</span>{' '}
                            {whatsappConfig.businessId}
                          </p>
                        )}
                        <p className="text-xs text-gray-500">
                          Last updated:{' '}
                          {new Date(whatsappConfig.updatedAt).toLocaleString()}
                        </p>
                      </div>

                      <div className="mt-6 border-t border-gray-100 pt-6">
                        <a
                          href={`https://business.facebook.com/billing_hub/accounts/details?asset_id=${whatsappConfig.whatsappBusinessAccountId}&business_id=${whatsappConfig.businessId}&placement=standalone&global_scope_id=${whatsappConfig.businessId}&account_type=whatsapp-business-account`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-500"
                        >
                          <svg
                            className="h-4 w-4 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                            />
                          </svg>
                          Manage Payment Method on Facebook
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppConfigurations;
